import { useState, useMemo } from 'react';
import { Download, Plus, X } from 'lucide-react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { bytesToBlob } from '@/lib/utils';

interface Item {
  description: string;
  qty: number;
  rate: number;
}

export function InvoiceGenerator() {
  const [from, setFrom] = useState('Utility OS Pvt Ltd\nBengaluru, KA · 560001\nGSTIN: 29ABCDE1234F1Z5');
  const [to, setTo] = useState('Acme Corp\nMumbai, MH · 400001\nGSTIN: 27ABCDE1234F1Z5');
  const [number, setNumber] = useState('INV-2026-0042');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [due, setDue] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [tax, setTax] = useState(18);
  const [notes, setNotes] = useState('Thank you for your business.');
  const [items, setItems] = useState<Item[]>([
    { description: 'Workspace plan — March', qty: 12, rate: 1500 },
    { description: 'Onboarding session', qty: 1, rate: 5000 },
  ]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.qty * i.rate, 0);
    const taxAmt = (subtotal * tax) / 100;
    return { subtotal, tax: taxAmt, total: subtotal + taxAmt };
  }, [items, tax]);

  function update(i: number, patch: Partial<Item>) {
    setItems((arr) => arr.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  }
  function add() {
    setItems((a) => [...a, { description: '', qty: 1, rate: 0 }]);
  }
  function remove(i: number) {
    setItems((a) => a.filter((_, idx) => idx !== i));
  }

  async function exportPdf() {
    const doc = await PDFDocument.create();
    const page = doc.addPage([595, 842]); // A4 in points
    const { width, height } = page.getSize();
    const font = await doc.embedFont(StandardFonts.Helvetica);
    const bold = await doc.embedFont(StandardFonts.HelveticaBold);

    const draw = (text: string, x: number, y: number, opts: { size?: number; font?: typeof font; color?: ReturnType<typeof rgb>; max?: number } = {}) => {
      const f = opts.font ?? font;
      const lines = text.split('\n');
      lines.forEach((line, i) => {
        page.drawText(line, {
          x,
          y: y - i * (opts.size ?? 11) * 1.4,
          font: f,
          size: opts.size ?? 11,
          color: opts.color ?? rgb(0.1, 0.1, 0.12),
        });
      });
    };

    // Header
    draw('INVOICE', 40, height - 60, { size: 28, font: bold });
    draw(number, width - 200, height - 60, { size: 14, font: bold });
    draw(`Issued: ${date}`, width - 200, height - 90, { color: rgb(0.4, 0.4, 0.45) });
    draw(`Due:    ${due}`, width - 200, height - 105, { color: rgb(0.4, 0.4, 0.45) });

    // Parties
    draw('From', 40, height - 150, { size: 9, font: bold, color: rgb(0.5, 0.5, 0.55) });
    draw(from, 40, height - 168);
    draw('Bill to', width / 2, height - 150, { size: 9, font: bold, color: rgb(0.5, 0.5, 0.55) });
    draw(to, width / 2, height - 168);

    // Items table
    const tableTop = height - 280;
    const col = { desc: 40, qty: 360, rate: 420, total: 510 };
    page.drawRectangle({ x: 40, y: tableTop - 6, width: width - 80, height: 22, color: rgb(0.95, 0.95, 0.97) });
    draw('DESCRIPTION', col.desc + 4, tableTop, { size: 9, font: bold });
    draw('QTY', col.qty, tableTop, { size: 9, font: bold });
    draw('RATE', col.rate, tableTop, { size: 9, font: bold });
    draw('AMOUNT', col.total, tableTop, { size: 9, font: bold });

    let y = tableTop - 28;
    items.forEach((it) => {
      draw(it.description, col.desc + 4, y);
      draw(String(it.qty), col.qty, y);
      draw(`₹${it.rate.toFixed(2)}`, col.rate, y);
      draw(`₹${(it.qty * it.rate).toFixed(2)}`, col.total, y);
      y -= 18;
    });

    // Totals
    const totalsX = width - 220;
    y -= 16;
    draw('Subtotal', totalsX, y);
    draw(`₹${totals.subtotal.toFixed(2)}`, totalsX + 110, y);
    y -= 16;
    draw(`Tax (${tax}%)`, totalsX, y);
    draw(`₹${totals.tax.toFixed(2)}`, totalsX + 110, y);
    y -= 22;
    draw('Total', totalsX, y, { font: bold, size: 14 });
    draw(`₹${totals.total.toFixed(2)}`, totalsX + 110, y, { font: bold, size: 14 });

    // Notes
    if (notes) {
      draw('Notes', 40, 100, { size: 9, font: bold, color: rgb(0.5, 0.5, 0.55) });
      draw(notes, 40, 84);
    }

    const bytes = await doc.save();
    saveAs(bytesToBlob(bytes), `${number}.pdf`);
    toast.success('Invoice exported');
  }

  return (
    <div>
      <ToolHeader
        toolId="invoice-generator"
        title="Invoice generator"
        description="Compose a clean invoice and export to PDF. Auto-calculates subtotal, tax, and total."
        actions={
          <Button size="sm" variant="primary" onClick={exportPdf}>
            <Download size={11} />
            Download PDF
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Field label="Invoice #">
              <Input value={number} onChange={(e) => setNumber(e.target.value)} />
            </Field>
            <Field label="Tax %">
              <Input
                type="number"
                value={tax}
                onChange={(e) => setTax(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
              />
            </Field>
            <Field label="Issue date">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label="Due date">
              <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
            </Field>
          </div>
          <Field label="From">
            <Textarea value={from} onChange={(e) => setFrom(e.target.value)} className="min-h-[80px]" />
          </Field>
          <Field label="Bill to" className="mt-3">
            <Textarea value={to} onChange={(e) => setTo(e.target.value)} className="min-h-[80px]" />
          </Field>
          <Field label="Notes" className="mt-3">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-[60px]" />
          </Field>
        </Card>

        <div className="space-y-3">
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Line items</h3>
              <Button size="sm" variant="ghost" onClick={add}>
                <Plus size={11} />
                Add
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_60px_80px_24px] gap-2 items-center">
                  <Input
                    placeholder="Description"
                    value={it.description}
                    onChange={(e) => update(i, { description: e.target.value })}
                  />
                  <Input
                    type="number"
                    value={it.qty}
                    onChange={(e) => update(i, { qty: Number(e.target.value) || 0 })}
                  />
                  <Input
                    type="number"
                    value={it.rate}
                    onChange={(e) => update(i, { rate: Number(e.target.value) || 0 })}
                  />
                  <button
                    onClick={() => remove(i)}
                    className="text-fg-subtle hover:text-danger size-7 rounded hover:bg-surface-hover transition flex items-center justify-center"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <div className="space-y-1.5 text-sm">
              <Row label="Subtotal" value={totals.subtotal} />
              <Row label={`Tax (${tax}%)`} value={totals.tax} />
              <div className="border-t border-border pt-2 mt-2">
                <Row label="Total" value={totals.total} big />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, big }: { label: string; value: number; big?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={big ? 'font-semibold' : 'text-fg-muted'}>{label}</span>
      <span className={`font-mono tabular-nums ${big ? 'text-2xl font-semibold' : ''}`}>
        ₹{value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </div>
  );
}

export default InvoiceGenerator;
